"use client";

import { useEffect, useState } from "react";

interface TocItem {
  id: string;
  label: string;
}

export function BrandTableOfContents() {
  const [items, setItems] = useState<TocItem[]>([]);
  const [activeId, setActiveId] = useState<string>("");

  useEffect(() => {
    // Scan the article for h2 headings to build the TOC dynamically.
    // Two patterns are supported (collected together in DOM order):
    // 1. <section id="..."><h2>...</h2></section> — brand/company pages
    // 2. <h2 id="...">...</h2> — blog pages (flat HTML, no section wrappers)
    const article = document.querySelector("article");
    if (!article) return;

    const tocItems: TocItem[] = [];
    const seenIds = new Set<string>();

    // Walk all h2 elements in document order
    const h2s = article.querySelectorAll("h2");
    h2s.forEach((h2) => {
      // Pattern 1: h2 inside a section[id] — use the section's id
      const parentSection = h2.closest("section[id]");
      if (parentSection) {
        const id = parentSection.id;
        if (id && !seenIds.has(id)) {
          const label = h2.textContent?.trim() || "";
          if (label) {
            tocItems.push({ id, label });
            seenIds.add(id);
          }
        }
        return;
      }
      // Pattern 2: bare h2 with its own id
      if (h2.id && !seenIds.has(h2.id)) {
        const label = h2.textContent?.trim() || "";
        if (label) {
          tocItems.push({ id: h2.id, label });
          seenIds.add(h2.id);
        }
      }
    });

    setItems(tocItems);

    // Set up scroll spy
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        }
      },
      { rootMargin: "-80px 0px -70% 0px", threshold: 0 }
    );

    for (const item of tocItems) {
      const el = document.getElementById(item.id);
      if (el) observer.observe(el);
    }

    return () => observer.disconnect();
  }, []);

  if (items.length === 0) return null;

  return (
    <nav className="max-h-[calc(100vh-7rem)] overflow-y-auto">
      <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
        On This Page
      </p>
      <ul className="space-y-1 border-l border-border">
        {items.map((item) => (
          <li key={item.id}>
            <a
              href={`#${item.id}`}
              className={`block pl-4 pr-2 py-1.5 text-base transition-colors -ml-px border-l-2 ${
                activeId === item.id
                  ? "border-accent text-accent font-medium"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {item.label}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
